/**
 * The spell-check worker's message protocol and the service behind it. The
 * service is plain code — no `self`, no `fetch`, no Hunspell import — so the
 * worker wires it to its globals and a test drives it with a tiny dictionary.
 */

/** Where the worker fetches a language's Hunspell files from (absolute URLs). */
export interface SpellDictionarySource {
  /** Cache key of the loaded dictionary, e.g. `de`. */
  readonly language: string;
  readonly aff: string;
  readonly dic: string;
}

export type SpellRequest =
  | {
      readonly id: number;
      readonly kind: 'check';
      readonly dictionary: SpellDictionarySource;
      readonly words: readonly string[];
    }
  | {
      readonly id: number;
      readonly kind: 'suggest';
      readonly dictionary: SpellDictionarySource;
      readonly word: string;
      readonly limit: number;
    };

export type SpellResponse =
  | { readonly id: number; readonly kind: 'checked'; readonly incorrect: readonly string[] }
  | { readonly id: number; readonly kind: 'suggested'; readonly suggestions: readonly string[] }
  | { readonly id: number; readonly kind: 'error'; readonly message: string };

/** What the service needs from a loaded dictionary (`hunspell.ts`). */
export interface SpellChecker {
  correct(word: string): boolean;
  suggest(word: string): string[];
}

export interface SpellServiceDeps {
  /** Fetch the `.aff` and `.dic` files of a dictionary. */
  load(source: SpellDictionarySource): Promise<{ aff: Uint8Array; dic: Uint8Array }>;
  /** Build a checker from them (Hunspell compiled to WebAssembly in the worker). */
  create(language: string, aff: Uint8Array, dic: Uint8Array): SpellChecker | Promise<SpellChecker>;
}

export interface SpellService {
  handle(request: SpellRequest): Promise<SpellResponse>;
}

/**
 * Loads each language once, on its first request, and keeps it: the worker
 * lives as long as the page, so a second document or a second editor pays
 * nothing. A failed load is forgotten, so the next request tries again.
 */
export function createSpellService(deps: SpellServiceDeps): SpellService {
  const checkers = new Map<string, Promise<SpellChecker>>();
  const suggestions = new Map<string, readonly string[]>();

  function checkerFor(source: SpellDictionarySource): Promise<SpellChecker> {
    let checker = checkers.get(source.language);
    if (!checker) {
      checker = deps.load(source).then(({ aff, dic }) => deps.create(source.language, aff, dic));
      checkers.set(source.language, checker);
      checker.catch(() => checkers.delete(source.language));
    }
    return checker;
  }

  return {
    async handle(request) {
      try {
        const checker = await checkerFor(request.dictionary);
        if (request.kind === 'check') {
          const incorrect = request.words.filter((word) => !checker.correct(word));
          return { id: request.id, kind: 'checked', incorrect };
        }
        const key = `${request.dictionary.language}\u0000${request.word}`;
        let found = suggestions.get(key);
        if (!found) {
          found = checker.suggest(request.word);
          suggestions.set(key, found);
        }
        return { id: request.id, kind: 'suggested', suggestions: found.slice(0, request.limit) };
      } catch (error) {
        return {
          id: request.id,
          kind: 'error',
          message: error instanceof Error ? error.message : String(error),
        };
      }
    },
  };
}
